import { CSSProperties, useEffect, useMemo, useState } from "react";
import moment, { Moment } from "moment";
import classNames from "classnames";
import useMeasure from "react-use-measure";
import { useIntl } from "react-intl";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import Button from "@/component/Button";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";

import { getDateActiveType } from "../helper";
import { DateRange } from "../type";


interface CalendarProps {
    useFuture?: boolean;
    month: Moment;
    activeDateRange: DateRange;
    onSelect: (clickedDate: Moment) => void;
    onHover: (hoveredDate?: Moment) => void;
    onChangeMonth: (newMonth: Moment) => void;
}

const Calendar: React.FC<CalendarProps> = (props) => {
    const { useFuture, month, activeDateRange, onChangeMonth, onSelect, onHover } = props;

    const today = moment().startOf('day');

    const monthDates = useMemo(() => {
        const monthFirstDate = month.clone().startOf('month');
        const calendarStartDate = monthFirstDate.clone().subtract(monthFirstDate.day(), 'days');

        return Array.from({ length: 42 }, (_, i) => {
            const date = calendarStartDate.clone().add(i, 'days');
            const diff = date.clone().startOf('month').diff(monthFirstDate, 'month');
            return {
                date: date,
                type: diff === 0 ? 'current' : diff > 0 ? 'next' : 'previous'
            };
        });
    }, [month]);

    const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    return (
        <div role="region"
             aria-label={month.format("MMM YYYY")}
             className="flex flex-col gap-1"
        >
            <CalendarHeader month={month}
                            onChange={onChangeMonth}
            />

            <div role="grid"
                 className="grid grid-cols-[30px_32px_32px_32px_32px_32px_30px] grid-rows-[repeat(7,28px)] gap-y-1"
            >
                <div role="row"
                     style={{ display: 'contents' }}
                >
                    {["S", "M", "T", "W", "T", "F", "S"].map((day, index) =>
                        <div key={index}
                             role="columnheader"
                             aria-label={DAY_LABELS[index]}
                             className="flex items-center justify-center text-chat42 text-[13px] font-[400] leading-none"
                        >
                            {day}
                        </div>
                    )}
                </div>
                {monthDates.map(({ date, type }) => (
                    <CalendarDate key={`${date.format()}_${type}`}
                                  date={date}
                                  activeRange={activeDateRange}
                                  isTargetMonth={type === 'current'}
                                  disabled={!useFuture && date.isAfter(today, 'day')}
                                  onClick={() => onSelect(date)}
                                  onHover={() => onHover(date)}
                    />
                ))}
            </div>
        </div>
    );
};


interface CalendarHeaderProps {
    month: Moment;
    onChange: (month: Moment) => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = (props) => {
    const { month, onChange } = props;
    const intl = useIntl();

    const [parentRef, { width: parentWidth, left: parentLeft }] = useMeasure();
    const [menuButtonRef, { left: menuButtonLeft }] = useMeasure();
    const [position, setPosition] = useState<CSSProperties>({});

    const onClickChangeMonth = (direction: 'previous' | 'next') => {
        onChange(month.clone().add(direction === 'previous' ? -1 : 1, 'month'));
    };

    useEffect(() => {
        setPosition({
            width: parentWidth,
            transform: `translateX(${parentLeft - menuButtonLeft}px)`,
        });
    }, [parentWidth, parentLeft, menuButtonLeft]);

    return (
        <div ref={parentRef} className="h-10 px-3.5 flex items-center justify-between">
            <Button aria-label={intl.formatMessage({ id: 'i04085' })}
                    onClick={() => onClickChangeMonth('previous')}
            >
                <ChevronArrowIcon direction="left"
                                  className="stroke-chat42 size-3"
                />
            </Button>
            <Menu>
                <MenuButton ref={menuButtonRef}
                            aria-label={`Month: ${moment.monthsShort()[month.month()]} ${month.year()}`}
                            className="px-2 py-1 text-chat42 font-[600] text-[14px] rounded-[6px] data-[hover]:bg-chat42-10 data-[open]:bg-chat42-10"
                >
                    {`${moment.monthsShort()[month.month()]} ${month.year()}`}
                </MenuButton>
                <MenuItems transition
                           anchor="bottom start"
                           style={position}
                           className="dropdown-items-box dropdown-secondary mt-3.5 z-[1000] grid grid-cols-6 gap-1"
                >
                    {moment.monthsShort().map((monthStr, index) => (
                        <MenuItem key={index}
                                  as='button'
                                  className={classNames(
                                      "w-full h-input-default font-[400] text-[13px] text-black-500 leading-[1.5] whitespace-nowrap text-center",
                                      "data-[hover]:font-[500] data-[focus]:font-[500]"
                                  )}
                                  onClick={() => onChange(month.clone().month(index))}
                        >
                            {monthStr}
                        </MenuItem>
                    ))}
                </MenuItems>
            </Menu>
            <Button aria-label={intl.formatMessage({ id: 'i04086' })}
                    onClick={() => onClickChangeMonth('next')}
            >
                <ChevronArrowIcon direction="right"
                                  className="stroke-chat42 size-3"
                />
            </Button>
        </div>
    );
};

interface CalendarDateProps {
    date: Moment;
    activeRange: DateRange;
    isTargetMonth: boolean;
    disabled?: boolean;
    onClick: () => void;
    onHover: () => void;
}

const CalendarDate: React.FC<CalendarDateProps> = (props) => {
    const { date, activeRange, isTargetMonth, disabled, onHover, onClick } = props;

    const isSunday = date.day() === 0;
    const isSaturday = date.day() === 6;
    const activeType = getDateActiveType(date, activeRange);

    const rangeDateClassNames = useMemo(() => {
        if (!isTargetMonth) {
            return "text-black-300";
        }
        const startOrEndStyle = "bg-chat42 text-white";
        switch (activeType) {
            case "not_active":
                return "rounded-[6px] text-black-500 hover:bg-black-200";
            case "between":
                return "bg-chat42-10 text-black-500";
            case "start":
                return classNames(startOrEndStyle, "rounded-l-[50%]");
            case "end":
                return classNames(startOrEndStyle, "rounded-r-[50%]");
            case "start_end":
                return classNames(startOrEndStyle, "rounded-[50%]");
        }
    }, [isTargetMonth, activeType]);

    return (
        <div className={classNames(
                 "relative size-full flex items-center justify-center cursor-pointer border-0",
                 disabled
                    ? "text-black-300 !cursor-not-allowed"
                    : rangeDateClassNames,
             )}
             onClick={() => !disabled && onClick()}
             onMouseEnter={() => !disabled && onHover()}
        >
            <Button disabled={disabled}
                    aria-label={date.format("MMMM D, YYYY")}
                    aria-selected={activeType !== "not_active" && isTargetMonth ? true : undefined}
                    className={classNames(
                        "px-0.5 border-transparent text-[13px] font-[400] leading-none",
                        isTargetMonth && !isSunday && {
                            "border-s-0.5": true,
                            "border-s-chat42": activeType === "start",
                            "border-s-chat42-10": activeType === "between",
                        },
                        isTargetMonth && !isSaturday && {
                            "border-e-0.5": true,
                            "border-e-chat42": activeType === "end",
                            "border-e-chat42-10": activeType === "between",
                        }
                    )}
            >
                {date.date()}
            </Button>
        </div>
    );
};

export default Calendar;

